import {
  Button,
  Card,
  HStack,
  Icon,
  Input,
  Stack,
  Switch,
  Text,
  VStack,
  IconButton,
  useColorModeValue,
  Tooltip,
  Avatar,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import React, { FC, useEffect, useState } from 'react';
import { AnswerType, Question, QuestionType, Survey, SurveyStatus } from './types';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import QuestionEditor from './QuestionEditor';
import { MdDragIndicator } from 'react-icons/md';
import { FiCheck, FiFileText, FiLock, FiPlus, FiTrash2 } from 'react-icons/fi';
import { RiImageAddFill } from 'react-icons/ri';
import { useAutoAnimate } from '@formkit/auto-animate/react'
import { addRemoveAnimation } from '../../hooks/useAnimate';
import HeadingSection from '../HeadingSection';
import { useFetch } from 'use-http';
import { api, makeHeaders } from '../../util/api';
import { useNavigate, useParams } from 'react-router';
import Loading from '../Misc/Loading';
import { ERROR_MESSAGE, useNotify } from '../../hooks/useNotify';
import { Link } from 'react-router-dom';
import { useDialog } from '../../hooks/useDialog';

const SurveyEditor: FC = () => {
  const { id } = useParams();
  const isEditing = !!id;
  const notify = useNotify();
  const {
    createDialog,
    closeDialog,
    setLoading,
  } = useDialog();

  const [title, setTitle] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isClosed, setIsClosed] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [responses, setResponses] = useState(0);

  const navigate = useNavigate();
  const [parent] = useAutoAnimate(addRemoveAnimation);

  const handleDragEnd = async (e: any) => {
    if (!e.destination) return;
    const tempData = Array.from(questions);
    const [sourceData] = tempData.splice(e.source.index, 1);
    tempData.splice(e.destination.index, 0, sourceData);
    setQuestions(tempData);
  };

  const addQuestion = () => {
    setQuestions([...questions, {
      id: Date.now().toString(),
      value: 'Nueva pregunta',
      questionType: QuestionType.TEXT,
      answerType: AnswerType.SINGLE,
      answerOptions: [],
    }])
  }

  const removeQuestion = (id: string) => {
    const updatedQuestions = questions.filter((q) => q.id !== id);
    setQuestions(updatedQuestions);
  }

  const updateQuestion = (updatedQuestion: Question) => {
    const index = questions.findIndex(((q) => q.id === updatedQuestion.id));
    const updatedQuestions = [...questions];

    if (index < 0) return;
    updatedQuestions[index] = updatedQuestion;
    setQuestions(updatedQuestions);
  };

  const {
    error,
    cache,
    loading,
    response,
    get,
    del,
    post,
    patch,
  } = useFetch(`${api}/surveys`, makeHeaders());

  const saveHandler = async () => {
    const body = {
      title,
      content: { questions },
      status: isPublished ? SurveyStatus.PUBLISHED : SurveyStatus.DRAFT,
    };
    cache.clear();
    if (isEditing) {
      await patch(`/${id}`, body);

      if (!error && response.ok) {
        notify('Encuesta actualizada', 'success');
      } else {
        notify(ERROR_MESSAGE, 'error');
      }
    } else {
      const created = await post('/', body);
      if (!error && response.ok) {
        navigate(`/encuestas/${created.id}/editar`, {
          replace: true,
        });
        notify('Encuesta creada', 'success');
      } else {
        notify(ERROR_MESSAGE, 'error');
      }
    }
  };

  const refreshSurvey = async () => {
    const survey: Survey = await get(`/${id}`);

    if (!error && response.ok) {
      setTitle(survey.title);
      setQuestions(survey.content.questions);
      setIsPublished(survey.status === SurveyStatus.PUBLISHED);
      setIsClosed(survey.status === SurveyStatus.CLOSED);
      setResponses(survey.responses);
    } else {
      notify(ERROR_MESSAGE, 'error');
    }
  };

  const deleteSurvey = async () => {
    try {
      setLoading(true);
      await del(`/${id}`);
      if (!error && response.ok) {
        notify('Encuesta archivada', 'success');
        navigate('/encuestas', {
          replace: true,
        })
      } else {
        throw new Error();
      }
    } catch {
      notify('Error al archivar encuesta', 'error');
    } finally {
      setLoading(false);
      closeDialog();
    }
  };

  const closeSurvey = async () => {
    try {

      cache.clear();
      setLoading(true);
      await patch(`/${id}/close`);
      if (!error && response.ok) {
        notify('Encuesta cerrada', 'success');
        refreshSurvey();
      } else {
        throw new Error();
      }
    } catch {
      notify(ERROR_MESSAGE, 'error');
    } finally {
      setLoading(false);
      closeDialog();
    }
  };

  const deleteSurveyHandler = () => {
    createDialog({
      title: 'Archivar encuesta',
      content: '¿Estás seguro que quieres archivar la encuesta? Ya no se podrá acceder a ella.',
      confirmText: 'Archivar',
      onConfirm: deleteSurvey,
      onCancel: closeDialog,
    });
  };

  const closeSurveyHandler = async () => {
    createDialog({
      title: 'Cerrar encuesta',
      content: '¿Estás seguro que quieres cerrar la encuesta? Ya no se aceptarán respuestas.',
      confirmText: 'Cerrar encuesta',
      onConfirm: closeSurvey,
      onCancel: closeDialog,
    });
  };

  useEffect(() => {
    if (!isEditing) return;
    refreshSurvey();
  }, []); // eslint-disable-line

  const cannotEdit = !!responses;
  const canNotEditMessage = `La encuesta no puede cambiarse una vez que ya ha sido respondida.
  (${responses} ${responses > 1 ? 'respuestas' : 'respuesta'})`

  return (
    <Stack maxW='container.sm'>

      {isClosed && (
        <Alert status='error'>
          <AlertIcon />
          <AlertDescription>
            Esta encuesta está cerrada y no se aceptan más respuestas.
          </AlertDescription>
        </Alert>
      )}

      <Card p={4} mb={2} variant='outline'>
        <Loading isLoading={loading} dense />
        <HStack justify='space-between'>
          <HeadingSection title='Configuración de la encuesta ' />
          <HStack>
            {isEditing && (
              <Button
                variant='solid'
                size='sm'
                leftIcon={<FiTrash2 />}
                onClick={deleteSurveyHandler}
              >
                Archivar
              </Button>
            )}

            <Tooltip label={cannotEdit ? canNotEditMessage : 'Guardar cambios'}>
              <Button
                size='sm'
                colorScheme='brand'
                leftIcon={<FiCheck />}
                onClick={saveHandler}
                isLoading={loading}
                isDisabled={(!title && !loading) || cannotEdit}
              >
                Guardar
              </Button>
            </Tooltip>
          </HStack>
        </HStack>
        <HStack gap={4}>
          <Tooltip label='Cambiar foto'>
            <Avatar
              size='lg'
              bg={useColorModeValue('gray.200', 'gray.700')}
              cursor='pointer'
              icon={<Icon as={RiImageAddFill} color='gray.500' boxSize={5} />}
            />
          </Tooltip>
          <VStack align='start' w='full' mb={3} gap={1}>
            <Text
              fontSize='sm'
              fontWeight='semibold'
            >
              Título
            </Text>
            <Input
              placeholder='Nombre de la encuesta'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              isDisabled={cannotEdit}
            />
          </VStack>
        </HStack>
        {cannotEdit && (
          <>
            <Button
              variant='outline'
              leftIcon={<FiFileText />}
              as={Link}
              to={`/encuestas/${id}`}
              mb={3}
            >
              Ver respuestas
            </Button>

            {!isClosed && (
              <Button
                variant='outline'
                onClick={closeSurveyHandler}
                leftIcon={<FiLock />}
                mb={3}
                colorScheme='red'
              >
                Cerrar encuesta
              </Button>
            )}
          </>
        )}
        {!cannotEdit && (
          <HStack justify='end'>
            <Tooltip label='El publicar una encuesta ya no podrás hacer cambios en las preguntas'>
              <Text fontWeight='bold' color='gray.500' textTransform='uppercase' fontSize='xs'>Publicar</Text>
            </Tooltip>
            <Switch
              isChecked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
            />
          </HStack>
        )}
      </Card>

      <Card
        variant='outline'
        p={4}
        bg={useColorModeValue('gray.50', 'gray.700')}
      >
        <Text fontWeight='bold' color='gray.500' textTransform='uppercase' fontSize='xs' mb={3}>Preguntas de la encuesta</Text>

        {!questions.length && (
          <Text align='center' fontSize='xs' color='gray.500' mb={3} fontWeight='medium'>No hay preguntas</Text>
        )}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId='droppable-1'>
            {(provider) => (
              <div
                {...provider.droppableProps}
                ref={provider.innerRef}
              >
                <div ref={parent}>
                  {questions.map((item, index) => (
                    <Draggable
                      key={item.id}
                      draggableId={item.id}
                      index={index}
                      isDragDisabled={cannotEdit}
                    >
                      {(provider2) => (
                        <div
                          ref={provider2.innerRef}
                          {...provider2.draggableProps}
                        >
                          <HStack py={2}>
                            {!cannotEdit && (
                              <HStack {...provider2.dragHandleProps}>
                                <Icon as={MdDragIndicator} color='gray.500' boxSize={5} />
                              </HStack>
                            )}
                            <QuestionEditor
                              id={item.id}
                              index={index + 1}
                              question={item}
                              onUpdate={updateQuestion}
                              readOnly={cannotEdit}
                            />
                            {!cannotEdit && (
                              <IconButton
                                isRound
                                aria-label='delete-question'
                                icon={<FiTrash2 />}
                                variant='ghost'
                                colorScheme='gray'
                                color='gray.500'
                                onClick={() => removeQuestion(item.id)}
                              >
                              </IconButton>
                            )}
                          </HStack>
                        </div>
                      )}
                    </Draggable>
                  ))}
                </div>
                {provider.placeholder}

              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Card>

      <Tooltip label={!!responses ? canNotEditMessage : 'Agregar nueva pregunta'}>
        <Button
          variant='solid'
          colorScheme='brand'
          leftIcon={<FiPlus />}
          onClick={addQuestion}
          isDisabled={loading || !!responses}
        >
          Agregar pregunta
        </Button>
      </Tooltip>
    </Stack>
  )
};

export default SurveyEditor;
